import React from 'react';

import { Extension } from '@atlassian/forge-ui';

import {
  ForgeModuleType,
  ForgeRepoPullRequestCardExtensionPointProps,
  ForgeRepoCodeOverviewCardExtensionPointProps,
  ForgeRepoCodeOverviewActionExtensionPointAppsListProps,
  ForgeRepoCodeOverviewActionExtensionSingleAppProps,
  ForgeRepoPullRequestActionExtensionPointAppsListProps,
  ForgeRepoPullRequestActionExtensionSingleAppProps,
  ForgeRepoMainMenuPageExtensionPointProps,
  ForgeRepoSettingsMenuPageExtensionPointProps,
  ForgePageExtensionPointProps,
  ForgeWorkspaceSettingsMenuPageExtensionPointProps,
  ForgeRepoPullRequestOverviewPanelExtensionPointProps,
} from '../types';

import { ForgeExtensionPoint } from './ForgeExtensionPoint';
import { ForgeExtensionPointAppsList } from './ForgeExtensionPointAppsList';
import { ForgeExtensionSingleApp } from './ForgeExtensionSingleApp';

/**
 * This is the file where all of the extension point components to be used to display
 * Forge UI apps live. See README on how to create and use  a new extension point.
 */

export const ForgeRepoPullRequestCardExtensionPoint = ({
  pullRequest,
  repository,
  ...extensionPointProps
}: ForgeRepoPullRequestCardExtensionPointProps): JSX.Element => (
  <ForgeExtensionPoint
    extensionData={{
      pullRequest: {
        id: pullRequest.id,
      },
      repository: {
        uuid: repository.uuid,
      },
      type: ForgeModuleType.REPO_PULL_REQUEST_CARD,
    }}
    {...extensionPointProps}
  />
);

export const ForgeRepoPullRequestOverviewPanelExtensionPoint = ({
  pullRequest,
  repository,
  ...extensionPointProps
}: ForgeRepoPullRequestOverviewPanelExtensionPointProps): JSX.Element => {
  return (
    <ForgeExtensionPoint
      extensionData={{
        pullRequest: {
          id: pullRequest.id,
        },
        repository: {
          uuid: repository.uuid,
        },
        type: ForgeModuleType.REPO_PULL_REQUEST_OVERVIEW_PANEL,
      }}
      {...extensionPointProps}
    />
  );
};

export const ForgeRepoCodeOverviewCardExtensionPoint = ({
  repository,
  ...extensionPointProps
}: ForgeRepoCodeOverviewCardExtensionPointProps): JSX.Element => (
  <ForgeExtensionPoint
    extensionData={{
      repository: {
        uuid: repository.uuid,
      },
      type: ForgeModuleType.REPO_CODE_OVERVIEW_CARD,
    }}
    {...extensionPointProps}
  />
);

export const ForgeRepoCodeOverviewActionExtensionPointAppsList = (
  props: ForgeRepoCodeOverviewActionExtensionPointAppsListProps
): JSX.Element => (
  <ForgeExtensionPointAppsList
    moduleType={ForgeModuleType.REPO_CODE_OVERVIEW_ACTION}
    {...props}
  />
);

export const ForgeRepoCodeOverviewActionExtensionSingleApp = ({
  repository,
  ...singleAppProps
}: ForgeRepoCodeOverviewActionExtensionSingleAppProps): JSX.Element => (
  <ForgeExtensionSingleApp
    extensionData={{
      repository: {
        uuid: repository.uuid,
      },
      type: ForgeModuleType.REPO_CODE_OVERVIEW_ACTION,
    }}
    {...singleAppProps}
  />
);

export const ForgeRepoPullRequestActionExtensionPointAppsList = (
  props: ForgeRepoPullRequestActionExtensionPointAppsListProps
): JSX.Element => (
  <ForgeExtensionPointAppsList
    moduleType={ForgeModuleType.REPO_PULL_REQUEST_ACTION}
    {...props}
  />
);

export const ForgeRepoPullRequestActionExtensionSingleApp = ({
  pullRequest,
  repository,
  ...singleAppProps
}: ForgeRepoPullRequestActionExtensionSingleAppProps): JSX.Element => (
  <ForgeExtensionSingleApp
    extensionData={{
      pullRequest: {
        id: pullRequest.id,
      },
      repository: {
        uuid: repository.uuid,
      },
      type: ForgeModuleType.REPO_PULL_REQUEST_ACTION,
    }}
    {...singleAppProps}
  />
);

export const extensionFilterForPageModules =
  (props: ForgePageExtensionPointProps) => (extension: Extension) =>
    extension.appId === props.appId &&
    extension.key === props.extensionkey &&
    // only match envKey if it's actually populated
    (!props.envKey || extension.environmentKey === props.envKey);

export const ForgeRepoMainMenuPageExtensionPoint = ({
  repository,
  ...extensionPointProps
}: ForgeRepoMainMenuPageExtensionPointProps): JSX.Element => (
  <ForgeExtensionPoint
    extensionData={{
      repository: {
        uuid: repository.uuid,
      },
      type: ForgeModuleType.REPO_MAIN_MENU_PAGE,
    }}
    extensionFilter={extensionFilterForPageModules(extensionPointProps)}
    {...extensionPointProps}
  />
);

export const ForgeRepoSettingsMenuPageExtensionPoint = ({
  repository,
  ...extensionPointProps
}: ForgeRepoSettingsMenuPageExtensionPointProps): JSX.Element => (
  <ForgeExtensionPoint
    extensionData={{
      repository: {
        uuid: repository.uuid,
      },
      type: ForgeModuleType.REPO_SETTINGS_MENU_PAGE,
    }}
    extensionFilter={extensionFilterForPageModules(extensionPointProps)}
    {...extensionPointProps}
  />
);

export const ForgeWorkspaceSettingsMenuPageExtensionPoint = (
  props: ForgeWorkspaceSettingsMenuPageExtensionPointProps
): JSX.Element => (
  <ForgeExtensionPoint
    extensionData={{
      workspace: {
        uuid: props.workspace.uuid,
      },
      type: ForgeModuleType.WORKSPACE_SETTINGS_MENU_PAGE,
    }}
    extensionFilter={extensionFilterForPageModules(props)}
    {...props}
  />
);
